import { Fragment, useEffect, useState } from 'react';
import Table from 'components/table';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';
import { ConfirmDialog } from 'components/settings/internalConfig/ConfirmDialog';
import { useProcessorState } from './hooks/useProcessorState';
import { useProcessorActions } from './hooks/useProcessorActions';
import { getColumns } from './columns';
import { preprocessTableData } from './utils';

type PendingAction =
  | { type: 'delete'; row: PAYMENT_CONFIG.IProcessors }
  | { type: 'activate'; row: PAYMENT_CONFIG.IProcessors }
  | null;

const ManageProcessors = (): JSX.Element => {
  const {
    paymentConfig,
    toFetchAllPaymentSolutions,
    toUpdatePaymentSolutions,
    toDeletePaymentSolutions,
    toFetchCardReaderValues,
    saveGlobalCardReaderValues,
    updateGlobalCardReaderValues,
  } = usePaymentConfig();

  const { allPaymentSolutions } = paymentConfig;

  const {
    deletedProcessors,
    setDeletedProcessors,
    tooltipStates,
    setTooltipStates,
    merchantIds,
    setMerchantIds,
  } = useProcessorState();

  const { handleToggle, handleDelete, handleActivate, togglingProcessorId } =
    useProcessorActions({
    toUpdatePaymentSolutions,
    toDeletePaymentSolutions,
    toFetchAllPaymentSolutions,
    toFetchCardReaderValues,
    saveGlobalCardReaderValues,
    updateGlobalCardReaderValues,
    merchantIds,
    setMerchantIds,
    setDeletedProcessors,
  });

  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const requestDelete = async (row: PAYMENT_CONFIG.IProcessors) => {
    setPendingAction({ type: 'delete', row });
  };

  const requestActivate = async (row: PAYMENT_CONFIG.IProcessors) => {
    setPendingAction({ type: 'activate', row });
  };

  const confirmPendingAction = () => {
    if (pendingAction?.type === 'delete') {
      handleDelete(pendingAction.row);
    } else if (pendingAction?.type === 'activate') {
      handleActivate(pendingAction.row);
    }
  };

  // Fetch all processors on mount
  useEffect(() => {
    toFetchAllPaymentSolutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Table columns configuration — delete/activate open the ConfirmDialog
  // rather than firing immediately. The real handlers run from onConfirm.
  const tableColumns = getColumns({
    deletedProcessors,
    tooltipStates,
    setTooltipStates,
    merchantIds,
    setMerchantIds,
    handleToggle,
    handleDelete: requestDelete,
    handleActivate: requestActivate,
    togglingProcessorId,
  });

  // Process table data
  const tableData = preprocessTableData(
    allPaymentSolutions?.data || [],
    deletedProcessors
  );

  return (
    <Fragment>
      <p className="font-normal text-sm leading-5 text-gray-blue-70">
        Below is a list of the payment solutions you can enable/disable.
      </p>

      <div className="mt-3">
        <Table
          data={tableData}
          columns={tableColumns}
          isEmpty={!tableData.length}
          theme={{
            tableWidth: '900px',
            showTableBorder: true,
          }}
        />
      </div>

      <ConfirmDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
        title={
          pendingAction?.type === 'delete'
            ? `Delete ${pendingAction.row.processorName}?`
            : pendingAction?.type === 'activate'
              ? `Reactivate ${pendingAction.row.processorName}?`
              : ''
        }
        description={
          pendingAction?.type === 'delete'
            ? `This will remove ${pendingAction.row.processorName} from the payment options.`
            : pendingAction?.type === 'activate'
              ? `Are you sure you want to reactivate ${pendingAction.row.processorName}?`
              : ''
        }
        confirmLabel={pendingAction?.type === 'delete' ? 'Delete' : 'Activate'}
        confirmVariant={pendingAction?.type === 'delete' ? 'destructive' : 'default'}
        onConfirm={confirmPendingAction}
      />
    </Fragment>
  );
};

export default ManageProcessors;
